/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect } from 'react';

import { Grid } from '@mui/material';
import dayjs from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import { useTranslation } from 'next-i18next';
import { Controller, useFormContext } from 'react-hook-form';
import InputMask from 'react-input-mask';

import { TextFieldWithLabel } from 'src/components/atoms/textField';
import { allowOnlyNumber } from 'src/helpers/utils';

dayjs.extend(customParseFormat);

type Props = {
  licenseFieldName: string;
  startDateFieldName: string;
  endDateFieldName: string;
  license?: string;
  startDate?: string;
  endDate?: string;
  disabled?: boolean;
  isTSI?: boolean;
};

const LicenseForm = ({
  licenseFieldName,
  startDateFieldName,
  endDateFieldName,
  license,
  startDate,
  endDate,
  disabled,
  isTSI = false,
}: Props) => {
  const { t } = useTranslation();
  const { control, setValue, getValues } = useFormContext();

  const getLicenseNumberPlaceHolder = () =>
    isTSI
      ? t('cpd_enrollment.license_information.tsi.placeholder.number')
      : t('cpd_enrollment.license_information.placeholder.number');

  const getMaxLength = () => (isTSI ? 6 : 10);

  useEffect(() => {
    if (license) {
      setValue(licenseFieldName, getValues(licenseFieldName) ? getValues(licenseFieldName) : license);
    }
    if (startDate) {
      setValue(
        startDateFieldName,
        getValues(startDateFieldName) ? getValues(startDateFieldName) : startDate?.split('/').join(' / '),
      );
    }
    if (endDate) {
      setValue(
        endDateFieldName,
        getValues(endDateFieldName) ? getValues(endDateFieldName) : endDate?.split('/').join(' / '),
      );
    }
  }, []);

  return (
    <Grid container alignItems="baseline" spacing={2}>
      <Grid xs={12} md={6} item>
        <Controller
          name={licenseFieldName}
          control={control}
          rules={{
            required: {
              value: true,
              message: t('cpd_enrollment.license_information.error.number'),
            },
            minLength: {
              value: getMaxLength(),
              message: t('cpd_enrollment.license_information.error.format'),
            },
            maxLength: {
              value: getMaxLength(),
              message: t('cpd_enrollment.license_information.error.format'),
            },
            pattern: {
              value: /^[0-9]*$/,
              message: t('cpd_enrollment.license_information.error.number'),
            },
          }}
          render={({ field, fieldState: { invalid, error } }) => (
            <TextFieldWithLabel
              {...field}
              placeholder={getLicenseNumberPlaceHolder()}
              error={invalid}
              inputProps={{ maxLength: getMaxLength() }}
              helperText={error?.message}
              label={t('cpd_enrollment.license_information.number')}
              required
              disabled={disabled}
              onChange={(e) => field.onChange(allowOnlyNumber(e.target.value))}
            />
          )}
        />
      </Grid>
      <Grid sx={{ display: { xs: 'none', md: 'block' } }} md={6} item />
      <Grid xs={12} md={6} item>
        <Controller
          name={startDateFieldName}
          control={control}
          rules={{
            // @ts-expect-error
            validate: (value: string) => {
              if (!value || value === t('cpd_enrollment.license_information.placeholder.date')) {
                return t('cpd_enrollment.license_information.error.start_date');
              }
              if (!dayjs(value, 'DD / MM / YYYY', true).isValid()) {
                return t('cpd_enrollment.license_information.error.wrong_date_format');
              }
              return true;
            },
          }}
          render={({ field, fieldState: { invalid, error } }) => (
            <InputMask
              mask="99 / 99 / 9999"
              maskPlaceholder={t('cpd_enrollment.license_information.placeholder.date')}
              alwaysShowMask
              {...field}
              disabled={disabled}
            >
              <TextFieldWithLabel
                required
                error={invalid}
                helperText={error?.message}
                label={t('cpd_enrollment.license_information.start_date')}
              />
            </InputMask>
          )}
        />
      </Grid>
      <Grid xs={12} md={6} item>
        <Controller
          name={endDateFieldName}
          control={control}
          rules={{
            // @ts-expect-error
            validate: (value: string) => {
              if (!value || value === t('cpd_enrollment.license_information.placeholder.date')) {
                return t('cpd_enrollment.license_information.error.end_date');
              }
              if (!dayjs(value, 'DD / MM / YYYY', true).isValid()) {
                return t('cpd_enrollment.license_information.error.wrong_date_format');
              }
              return true;
            },
          }}
          render={({ field, fieldState: { invalid, error } }) => (
            <>
              <InputMask
                mask="99 / 99 / 9999"
                maskPlaceholder={t('cpd_enrollment.license_information.placeholder.date')}
                alwaysShowMask
                {...field}
                disabled={disabled}
              >
                <TextFieldWithLabel
                  required
                  error={invalid}
                  helperText={error?.message}
                  label={t('cpd_enrollment.license_information.end_date')}
                />
              </InputMask>
            </>
          )}
        />
      </Grid>
    </Grid>
  );
};

export default LicenseForm;
